import { useDocumentTitle } from "@uidotdev/usehooks";
import { type FC, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Icons, PanelOrientation, PanelPosition, ResizableTabPanels, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";
import { useShallow } from "zustand/shallow";

import { fetchFeatureData } from "./api/fetch.ts";
import { Canvas } from "./components/canvas/Canvas.tsx";
import { DevMode } from "./components/DevMode";
import { ShortcutsCard } from "./components/help/Shortcuts.tsx";
import { LocationSelectionDialog } from "./components/LocationSelectionDialog.tsx";
import { OverviewCard } from "./components/overview/Overview.tsx";
import { SearchCard } from "./components/search/SearchCard.tsx";
import { GenomeViewerStatusBarActions } from "./components/toolbar/GenomeViewerStatusBarActions.tsx";
import { GenomeViewerToolbar } from "./components/toolbar/GenomeViewerToolbar.tsx";
import { TrackGroupsListCard } from "./components/track-list/TrackGroupsList.tsx";
import { useGenomeViewerStore } from "./store/store.ts";
import {
  type FeatureLocation,
  InspectionPanelTabs,
  type NavigationFeature,
  StatusPanelTabs,
  type UrlEntityType,
} from "./types.ts";
import { loadFromFeature } from "./utils/load-from-feature.ts";
import { getGenomicFeatureTypeFromEntity } from "./utils/type-conversion.ts";

export const GenomeViewer: FC<{ syntenyId?: string }> = ({ syntenyId }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { syntenyOrgId } = useParams<"syntenyOrgId">();
  const [searchParams] = useSearchParams();

  const loadTracks = useGenomeViewerStore((state) => state.loadTracks);
  const reset = useGenomeViewerStore((state) => state.reset);
  const ready = useGenomeViewerStore((state) => !!state.canvas);
  const loadView = useGenomeViewerStore((state) => state.loadView);
  const loadedSyntenyOrganism = useGenomeViewerStore(useShallow((state) => state.syntenyOrganism));

  const [locationFeatures, setLocationFeatures] = useState<FeatureLocation[]>([]);
  const { overlayProps, state } = useOverlay();

  useDocumentTitle(pathname.includes("/visualizer/") ? "Synteny Viewer - Selector" : document.title);

  const { showBoundary } = useErrorBoundary();

  function handleFeatureSelection(selectedFeature: FeatureLocation) {
    const organismId = Number(searchParams.get("organismId"));
    const resultType = searchParams.get("resultType");

    const dataSyntenyId: number | undefined = syntenyId
      ? Number(syntenyId)
      : syntenyOrgId && Number(syntenyOrgId) !== loadedSyntenyOrganism.id
        ? Number(syntenyOrgId)
        : undefined;

    if (!dataSyntenyId || !organismId || !resultType) return;

    const feature: NavigationFeature = {
      id: selectedFeature.id,
      contigId: selectedFeature.contigId,
      orgId: organismId,
      type: getGenomicFeatureTypeFromEntity(resultType as UrlEntityType),
      subTypeId: selectedFeature.subTypeId,
      start: selectedFeature.range.start,
      stop: selectedFeature.range.stop,
    };

    useGenomeViewerStore.setState({
      needsRefresh: true,
      featureToNavigate: feature,
    });

    state.close();
  }

  // clean/delete the store
  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    if (!ready) return;
    const viewId = searchParams.get("viewid");
    const organismId = Number(searchParams.get("organismId"));
    const entityId = Number(searchParams.get("entityId"));
    const resultType = searchParams.get("resultType");
    const resultSubTypeId = Number(searchParams.get("resultSubTypeId"));

    async function getFeatureData() {
      const genomicType = getGenomicFeatureTypeFromEntity(resultType as UrlEntityType);
      const response = await fetchFeatureData(genomicType, entityId);

      if (!response || response.length === 0) {
        throw new Error(`No feature data found for entity ID: ${entityId}`);
      }

      return response.map((item) => ({ type: genomicType, ...item }));
    }

    const dataSyntenyId: number | undefined = syntenyId
      ? Number(syntenyId)
      : syntenyOrgId && Number(syntenyOrgId) !== loadedSyntenyOrganism.id
        ? Number(syntenyOrgId)
        : undefined;

    // Fallback to synteny viewer with synteny select in toolbar
    if (!dataSyntenyId) {
      reset();
      throw new Error("You must select a genome first");
    }

    // From saved view
    if (viewId) {
      // From feature table inside a saved view
      if (organismId && entityId && resultType) {
        getFeatureData()
          .then((features: FeatureLocation[]) => {
            const selectedFeature = features[0];

            loadView(Number(viewId), dataSyntenyId, showBoundary).then(() => {
              const genomicType = getGenomicFeatureTypeFromEntity(resultType as UrlEntityType);
              loadFromFeature(selectedFeature, organismId, genomicType, resultSubTypeId);
            });
            // If multiple locations of a feature are found, open a selection dialog
            if (features.length > 1) {
              setLocationFeatures(features);
              state.open();
            }
          })
          .catch(() => {
            loadView(Number(viewId), dataSyntenyId, showBoundary);
          });
      } else {
        loadView(Number(viewId), dataSyntenyId, showBoundary);
      }
      // From normal view
    } else {
      // From feature table inside a normal view
      if (organismId && entityId && resultType) {
        getFeatureData()
          .then((features) => {
            const initialContigs: number[] = [];
            const selectedFeature = features[0];

            initialContigs.push(Number(selectedFeature.contigId));
            loadTracks(dataSyntenyId, initialContigs, showBoundary).then(() => {
              const genomicType = getGenomicFeatureTypeFromEntity(resultType as UrlEntityType);
              loadFromFeature(selectedFeature, organismId, genomicType, resultSubTypeId);
            });
            // If multiple locations of a feature are found, open a selection dialog
            if (features.length > 1) {
              setLocationFeatures(features);
              state.open();
            }
          })
          .catch(() => {
            loadTracks(dataSyntenyId, [], showBoundary);
          });
      } else {
        loadTracks(dataSyntenyId, [], showBoundary);
      }
    }
    // loadedSyntenyOrganism.id must not be part of the dependency otherwise it will load twice
    // during the navigation on a group summary page as it unmounts before loading the next group
    // when using the pagination widget
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTracks, ready, navigate, syntenyOrgId, syntenyId, reset]);

  return (
    <ResizableTabPanels
      orientation={PanelOrientation.Horizontal}
      fixedPanel={PanelPosition.Trailing}
      defaultSize={350}
      minSize={240}
      maxSize={480}
      isContinuous
      style={{ width: "100%", height: "100%" }}
    >
      <ResizableTabPanels.FlexPanel>
        <VStack spacing={0} style={{ width: "100%", height: "100%" }}>
          <GenomeViewerToolbar />
          <ResizableTabPanels
            orientation={PanelOrientation.Vertical}
            fixedPanel={PanelPosition.Trailing}
            minSize={250}
            defaultSize={250}
            closedSize={39}
            isContinuous
            openOnTabPress
            tabsActions={[<GenomeViewerStatusBarActions key="status-bar-toggle" />]}
            defaultOpen={false}
            style={{ width: "100%", height: "100%" }}
          >
            <ResizableTabPanels.FlexPanel>
              <LocationSelectionDialog
                handleSelect={(feature: FeatureLocation) => handleFeatureSelection(feature)}
                state={state}
                overlayProps={overlayProps}
                features={locationFeatures}
              />
              <Canvas />
            </ResizableTabPanels.FlexPanel>
            <ResizableTabPanels.FixedPanel>
              <ResizableTabPanels.FixedPanelTab
                id={StatusPanelTabs.Help}
                title={StatusPanelTabs.Help}
                position={0}
                icon="keyboard"
              >
                <ShortcutsCard />
              </ResizableTabPanels.FixedPanelTab>
              {!!import.meta.env.DEV && (
                <ResizableTabPanels.FixedPanelTab
                  id={StatusPanelTabs.DevMode}
                  title={StatusPanelTabs.DevMode}
                  position={1}
                  icon={"code_blocks"}
                >
                  <DevMode />
                </ResizableTabPanels.FixedPanelTab>
              )}
            </ResizableTabPanels.FixedPanel>
          </ResizableTabPanels>
        </VStack>
      </ResizableTabPanels.FlexPanel>
      <ResizableTabPanels.FixedPanel showTabsLabel>
        <ResizableTabPanels.FixedPanelTab
          icon={<img src={Icons.Action.GenomeViewer} width={20} height={20} alt="" />}
          title="Synteny"
          position={0}
          id={InspectionPanelTabs.GenomeViewer}
        >
          <>
            <OverviewCard />
            <TrackGroupsListCard />
          </>
        </ResizableTabPanels.FixedPanelTab>
        <ResizableTabPanels.FixedPanelTab icon="search" title="Search" position={1} id={InspectionPanelTabs.Search}>
          <SearchCard />
        </ResizableTabPanels.FixedPanelTab>
      </ResizableTabPanels.FixedPanel>
    </ResizableTabPanels>
  );
};
